/**
 * The bridge's HLS namespace: a loader, not a player.
 *
 * The player itself is `hls-player.ts`, emitted as a separate bundle because
 * hls.js is ~400 KB and the bridge is held to 64 KB — it is on the boot path
 * of every call, and that budget is the reason this app does not ship a second
 * framework. Everything here is small enough to sit on that path; the large
 * thing is fetched the first time somebody actually opens a channel, and never
 * for a visitor who does not.
 */

import type { HlsApi, HlsAttachment, HlsAttachOptions } from './types.js';

/**
 * Where the player bundle is served from.
 *
 * Resolved against `document.baseURI` — which is `<base href>` in
 * `index.html`, the same thing that makes `<script src="media/
 * rafeekh-media.js">` on line 390 of that file resolve. So this points at the
 * sibling of the bundle that is running, wherever the app is deployed and
 * whatever base href Flutter was built with, without any configuration to get
 * wrong.
 *
 * `import.meta.url` would be the more obvious way to say "next to me" and is
 * deliberately not used: ts-jest transforms this package to CommonJS, where
 * `import.meta` is a syntax error, so it would have made the whole package
 * untestable to save nothing.
 */
const PLAYER_PATH = 'media/rafeekh-hls.js';

function playerUrl(): string {
  return new URL(PLAYER_PATH, document.baseURI).href;
}

/** Resolves once, and is reused — two channels must not fetch 400 KB twice. */
let loading: Promise<void> | null = null;

async function ensureLoaded(): Promise<void> {
  if (window.RafeekhHls) return;
  if (!loading) {
    // A dynamic import of a runtime-computed URL, which esbuild leaves alone
    // rather than inlining — which is the whole point.
    loading = import(/* @vite-ignore */ playerUrl()).then(() => undefined);
  }
  await loading;
}

export const hls: HlsApi = {
  /**
   * Whether this browser can play a live stream at all.
   *
   * Answered without loading the player: Safari says so through
   * `canPlayType`, and everywhere else it comes down to Media Source
   * Extensions, which is one property check. A UI that has to download
   * 400 KB before it can tell a listener "not supported" is a bad UI.
   */
  isSupported(): boolean {
    if (typeof document === 'undefined') return false;
    const probe = document.createElement('video');
    if (probe.canPlayType('application/vnd.apple.mpegurl')) return true;
    const mse = (window as unknown as { MediaSource?: unknown }).MediaSource;
    return typeof mse !== 'undefined';
  },

  async attach(video: HTMLVideoElement, url: string, options: HlsAttachOptions = {}): Promise<HlsAttachment> {
    await ensureLoaded();
    const player = window.RafeekhHls;
    if (!player) {
      options.onFatal?.({ fatal: true, code: 'PLAYER_LOAD_FAILED', message: 'The player could not be loaded' });
      return { engine: 'hlsjs', destroy() {} };
    }
    return player.attach(video, url, options);
  },
};
